import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import JsBarcode from "jsbarcode";
import api, { formatErr } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { isCampusBound } from "../lib/roles";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Printer } from "lucide-react";
import { campusLabel } from "../lib/campus";

const CAMPUSES = ["YPJ Kuala Kencana", "YPJ TPRA"];

/* Long Code 128 bar row: wide bars and a generous quiet zone so it scans cleanly even off a computer screen. */
function BarRow({ value }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const svg = ref.current;
    try {
      JsBarcode(svg, value, { format: "CODE128", width: 3, height: 70, displayValue: false, margin: 16, background: "#ffffff" });
      // every label gets the same box: the bars stretch to fill it, whatever the code length
      const w = parseFloat(svg.getAttribute("width")); const h = parseFloat(svg.getAttribute("height"));
      svg.removeAttribute("width"); svg.removeAttribute("height"); svg.removeAttribute("style");
      svg.setAttribute("viewBox", `0 0 ${w} ${h}`); svg.setAttribute("preserveAspectRatio", "none");
    } catch (e) { svg.innerHTML = ""; }
  }, [value]);
  return <svg ref={ref} className="w-full h-32 block" role="img" aria-label={value} />;
}

export default function QrLabelsPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const loc = useLocation();
  // opened from a new asset, the asset page or ticked rows in the list: print exactly those assets
  const pickedIds = useMemo(() => (loc.state?.ids?.length ? loc.state.ids : (new URLSearchParams(loc.search).get("ids") || "").split(",").filter(Boolean)), [loc.state, loc.search]);
  const [campus, setCampus] = useState(user?.campus && CAMPUSES.includes(user.campus) ? user.campus : CAMPUSES[0]);
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(new Set());
  // "loan": IT devices that are lent out. "fixed": furniture, wall clocks and other assets that stay put (sticker for asset checks).
  const [mode, setMode] = useState("loan");
  const [cat, setCat] = useState("");

  useEffect(() => {
    setQ(""); setCat("");
    if (pickedIds.length) {
      api.get("/assets/label-list", { params: { ids: pickedIds.join(",") } })
        .then((r) => { setAssets(r.data); setSel(new Set(r.data.map((a) => a.id))); })
        .catch((e) => toast.error(formatErr(e)));
      return;
    }
    api.get("/assets/label-list", { params: { campus, group: mode } })
      .then((r) => { setAssets(r.data); setSel(mode === "loan" ? new Set(r.data.map((a) => a.id)) : new Set()); })
      .catch((e) => toast.error(formatErr(e)));
  }, [campus, mode, pickedIds]);

  const categories = useMemo(() => [...new Set(assets.map((a) => a.category).filter(Boolean))].sort(), [assets]);
  const shown = useMemo(() => assets.filter((a) => (!cat || a.category === cat)
    && `${a.name} ${a.asset_tag} ${a.location || ""}`.toLowerCase().includes(q.toLowerCase())), [assets, q, cat]);
  const chosen = shown.filter((a) => sel.has(a.id));

  return (
    <div className="p-6 lg:p-10 max-w-[1100px] mx-auto" data-testid="qr-labels-page">
      <style>{`@media print { body * { visibility: hidden; } #qr-sheet, #qr-sheet * { visibility: visible; } #qr-sheet { position: absolute; left: 0; top: 0; width: 100%; grid-template-columns: repeat(2, 1fr) !important; column-gap: 2cm !important; } }`}</style>
      <Link to="/scan" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> {t("qr_title")}</Link>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">{t("qr_print_labels")}</h1>
      <p className="text-sm text-slate-500 mt-2">{t("qr_labels_hint")}</p>
      {pickedIds.length > 0 && <p className="text-sm text-blue-900 bg-blue-50 border border-blue-200 px-3 py-2 mt-4" data-testid="qr-picked-hint">{t("qr_picked_hint").replace("{n}", assets.length)} <Link to="/loans/labels" className="underline ml-1">{t("qr_picked_all")}</Link></p>}
      <div className={`flex mt-5 ${pickedIds.length ? "hidden" : ""}`} role="tablist">
        {[["loan", "qr_mode_loan"], ["fixed", "qr_mode_fixed"]].map(([m, k]) => (
          <button key={m} role="tab" aria-selected={mode === m} onClick={() => setMode(m)} data-testid={`qr-mode-${m}`}
            className={`px-4 py-2 text-sm border border-slate-300 ${mode === m ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{t(k)}</button>))}
      </div>
      {mode === "fixed" && <p className="text-xs text-slate-500 mt-2">{t("qr_fixed_hint")}</p>}
      <div className="flex gap-3 flex-wrap items-center my-5">
        {!pickedIds.length && <select className="border border-slate-200 bg-white px-3 py-2 text-sm h-10" value={campus} disabled={isCampusBound(user?.role)} onChange={(e) => setCampus(e.target.value)}>
          {CAMPUSES.map((c) => <option key={c} value={c}>{campusLabel(c)}</option>)}</select>}
        {mode === "fixed" && !pickedIds.length && <select className="border border-slate-200 bg-white px-3 py-2 text-sm h-10" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="qr-category">
          <option value="">{t("qr_all_categories")}</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</select>}
        <Input className="w-64 rounded-none" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ln_search")} />
        <Button variant="outline" className="rounded-none" onClick={() => setSel(new Set(shown.map((a) => a.id)))}>{t("qr_select_all")}</Button>
        {mode === "fixed" && <Button variant="outline" className="rounded-none" onClick={() => setSel(new Set())}>{t("qr_select_none")}</Button>}
        <Button className="rounded-none ml-auto" onClick={() => window.print()} data-testid="qr-print"><Printer className="w-4 h-4 mr-2" /> {t("qr_print")} ({chosen.length})</Button>
      </div>
      <div className="border border-slate-200 bg-white p-3 mb-6 max-h-48 overflow-y-auto grid sm:grid-cols-2 gap-1 text-sm">
        {shown.map((a) => (
          <label key={a.id} className="flex items-center gap-2"><input type="checkbox" checked={sel.has(a.id)} onChange={(e) => { const s = new Set(sel); e.target.checked ? s.add(a.id) : s.delete(a.id); setSel(s); }} />
            <span className="truncate">{a.name}</span><span className="font-mono text-xs text-slate-400">{a.asset_tag}</span></label>))}
        {shown.length === 0 && <div className="text-slate-500 p-2">{t("ln_no_borrowable")}</div>}
      </div>
      <div id="qr-sheet" className="grid grid-cols-1 sm:grid-cols-2 gap-y-3" style={{ columnGap: "2cm" }}>
        {chosen.map((a) => (
          <div key={a.id} className="border border-slate-300 p-2 text-center break-inside-avoid" data-testid={`qr-label-${a.asset_tag}`}>
            <BarRow value={a.asset_tag} />
            <div className="text-sm font-bold text-slate-900 leading-tight mt-0.5 truncate">{[a.name !== a.asset_tag ? a.name : null, a.asset_code || null, a.asset_tag, mode === "fixed" ? (a.location || a.assigned_to_name || campusLabel(a.campus)) : (a.unit ? t(`unit_${a.unit}`) : campusLabel(a.campus))].filter(Boolean).join(" | ")}</div>
          </div>))}
      </div>
    </div>
  );
}
